import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { 
  Loader2, 
  CheckCircle, 
  XCircle, 
  RefreshCw, 
  Globe, 
  AlertTriangle,
  Clock,
  BarChart3,
  ExternalLink,
  Search
} from "lucide-react";
import { debounce } from "lodash";
import SessionEditor from "../components/SessionEditor";

const API_URL = process.env.REACT_APP_API_URL || "https://api.littleexplorers.help";

function StatusBadge({ status }) {
  const styles = {
    queued: "bg-gray-500/20 text-gray-400",
    crawling: "bg-blue-500/20 text-blue-400",
    extracting: "bg-purple-500/20 text-purple-400",
    review: "bg-yellow-500/20 text-yellow-400",
    approved: "bg-green-500/20 text-green-400",
    rejected: "bg-red-500/20 text-red-400",
    failed: "bg-red-500/20 text-red-400",
  };

  const icons = {
    queued: Clock,
    crawling: Globe,
    extracting: Loader2,
    review: AlertTriangle,
    approved: CheckCircle,
    rejected: XCircle,
    failed: XCircle,
  };

  const Icon = icons[status] || Clock;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.queued}`}>
      <Icon className={`w-3 h-3 ${status === 'extracting' ? 'animate-spin' : ''}`} />
      <span className="capitalize">{status}</span>
    </span>
  );
}

function ConfidenceBadge({ score }) {
  if (!score && score !== 0) return null;
  
  let color = "text-red-400";
  if (score >= 90) color = "text-green-400";
  else if (score >= 70) color = "text-yellow-400";
  else if (score >= 50) color = "text-orange-400";

  return (
    <span className={`font-bold ${color}`}>
      {score}%
    </span>
  );
}

function ReviewCard({ job, onApprove, onReject, onRetry, getAccessToken }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState(() => {
    // Initialize with sessions from extracted programs
    const programs = (job.extractedPrograms || []).map(p => ({
      ...p,
      sessions: p.sessions || []
    }));
    return {
      orgData: job.extractedOrgData || {},
      programs,
    };
  });
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [duplicateWarnings, setDuplicateWarnings] = useState([]);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);

  const handleApprove = () => {
    onApprove(job.id, editedData);
  };

  const handleReject = () => {
    if (!rejectReason.trim()) return;
    onReject(job.id, rejectReason);
  };

  const updateOrgField = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      orgData: { ...prev.orgData, [field]: value }
    }));
  };

  const updateProgramField = (index, field, value) => {
    setEditedData(prev => ({
      ...prev,
      programs: prev.programs.map((p, i) => 
        i === index ? { ...p, [field]: value } : p
      )
    }));
  };

  // Session management functions
  const updateSessionField = (programIdx, sessionIdx, field, value) => {
    setEditedData(prev => ({
      ...prev,
      programs: prev.programs.map((p, i) => 
        i === programIdx 
          ? { 
              ...p, 
              sessions: (p.sessions || []).map((s, j) => 
                j === sessionIdx ? { ...s, [field]: value } : s
              )
            } 
          : p
      )
    }));
  };

  const removeProgram = (programIdx) => {
    if (window.confirm('Are you sure you want to delete this program and all its sessions?')) {
      setEditedData(prev => ({
        ...prev,
        programs: prev.programs.filter((_, i) => i !== programIdx)
      }));
    }
  };

  const addSession = (programIdx) => {
    const newSession = {
      name: '',
      description: '',
      startDate: '',
      endDate: '',
      daysOfWeek: '',
      startTime: '',
      endTime: '',
      capacity: undefined,
      priceTotal: undefined,
      registrationUrl: '',
      isTryout: false,
    };
    setEditedData(prev => ({
      ...prev,
      programs: prev.programs.map((p, i) => 
        i === programIdx 
          ? { ...p, sessions: [...(p.sessions || []), newSession] }
          : p
      )
    }));
  };

  const removeSession = (programIdx, sessionIdx) => {
    setEditedData(prev => ({
      ...prev,
      programs: prev.programs.map((p, i) => 
        i === programIdx 
          ? { ...p, sessions: (p.sessions || []).filter((_, j) => j !== sessionIdx) }
          : p
      )
    }));
  };

  // Validate all data before approval
  const validateAll = () => {
    const errors = [];
    
    editedData.programs.forEach((program, pIdx) => {
      if (!program.name) {
        errors.push(`Program ${pIdx + 1}: Name is required`);
      }
      
      (program.sessions || []).forEach((session, sIdx) => {
        if (!session.startDate) {
          errors.push(`Program "${program.name || pIdx + 1}", Session ${sIdx + 1}: Start date is required`);
        }
        if (!session.endDate) {
          errors.push(`Program "${program.name || pIdx + 1}", Session ${sIdx + 1}: End date is required`);
        }
        if (session.startDate && session.endDate) {
          const start = new Date(session.startDate);
          const end = new Date(session.endDate);
          if (end < start) {
            errors.push(`Program "${program.name || pIdx + 1}", Session ${sIdx + 1}: End date must be after start date`);
          }
        }
      });
    });
    
    return errors;
  };

  const handleLocalApprove = () => {
    const errors = validateAll();
    if (errors.length > 0) {
      setValidationErrors(errors);
      alert(`Please fix the following errors:\n\n${errors.slice(0, 5).join('\n')}${errors.length > 5 ? `\n...and ${errors.length - 5} more` : ''}`);
      return;
    }
    setValidationErrors([]);
    onApprove(job.id, editedData);
  };

  // Check for duplicate sessions
  const checkForDuplicates = async () => {
    if (!job.organizationId) return;
    
    setCheckingDuplicates(true);
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_URL}/v2/ingestion/check-duplicates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          organizationId: job.organizationId,
          programs: editedData.programs,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setDuplicateWarnings(result.duplicates || []);
        
        if (result.hasDuplicates) {
          const highConf = result.highConfidenceDuplicates;
          const potential = result.potentialDuplicates;
          alert(`Duplicate Check Results:\n\n${highConf} high-confidence duplicates will be skipped\n${potential} potential duplicates flagged\n\nReview the highlighted sessions below.`);
        } else {
          alert('No potential duplicates found!');
        }
      }
    } catch (error) {
      console.error('Error checking duplicates:', error);
    } finally {
      setCheckingDuplicates(false);
    }
  };

  return (
    <div className="glass-card p-6 mb-4">
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-bold text-white">{job.url}</h3>
            <a 
              href={job.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <StatusBadge status={job.status} />
            {job.confidenceScores?.overall && (
              <span className="text-gray-400">
                Confidence: <ConfidenceBadge score={job.confidenceScores.overall} />
              </span>
            )}
            {job.retryCount > 0 && (
              <span className="text-orange-400 text-xs">
                Retry #{job.retryCount}
              </span>
            )}
          </div>
        </div>
        <div className="text-right text-xs text-gray-500">
          <div>Created: {new Date(job.createdAt).toLocaleString()}</div>
          {job.extractedAt && (
            <div>Extracted: {new Date(job.extractedAt).toLocaleString()}</div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {job.errorMessage && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4">
          <p className="text-red-400 text-sm">{job.errorMessage}</p>
        </div>
      )}

      {/* Extracted Data */}
      {job.status === 'review' && (
        <div className="space-y-4">
          {/* Organization Data */}
          <div className="bg-gray-800/50 rounded-lg p-4">
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-white font-medium">Organization</h4>
              <button
                type="button"
                onClick={() => setIsEditing(!isEditing)}
                className="text-xs text-indigo-400 hover:text-indigo-300"
              >
                {isEditing ? 'Done Editing' : 'Edit'}
              </button>
            </div>
            
            {isEditing ? (
              <div className="space-y-2">
                <input
                  type="text"
                  value={editedData.orgData.name || ''}
                  onChange={(e) => updateOrgField('name', e.target.value)}
                  placeholder="Organization Name"
                  className="w-full px-3 py-2 bg-gray-700 rounded text-white text-sm"
                />
                <input
                  type="text"
                  value={editedData.orgData.type || ''}
                  onChange={(e) => updateOrgField('type', e.target.value)}
                  placeholder="Type"
                  className="w-full px-3 py-2 bg-gray-700 rounded text-white text-sm"
                />
                <input
                  type="text"
                  value={editedData.orgData.city || ''}
                  onChange={(e) => updateOrgField('city', e.target.value)}
                  placeholder="City"
                  className="w-full px-3 py-2 bg-gray-700 rounded text-white text-sm"
                />
                <input
                  type="text"
                  value={editedData.orgData.state || ''}
                  onChange={(e) => updateOrgField('state', e.target.value)}
                  placeholder="State"
                  className="w-full px-3 py-2 bg-gray-700 rounded text-white text-sm"
                />
                <textarea
                  value={editedData.orgData.description || ''}
                  onChange={(e) => updateOrgField('description', e.target.value)}
                  placeholder="Description"
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-700 rounded text-white text-sm"
                />
              </div>
            ) : (
              <div className="space-y-1 text-sm">
                <div><span className="text-gray-400">Name:</span> <span className="text-white">{editedData.orgData.name || 'N/A'}</span></div>
                <div><span className="text-gray-400">Type:</span> <span className="text-white">{editedData.orgData.type || 'N/A'}</span></div>
                <div><span className="text-gray-400">Location:</span> <span className="text-white">{editedData.orgData.city}{editedData.orgData.city && editedData.orgData.state ? ', ' : ''}{editedData.orgData.state}</span></div>
                {editedData.orgData.description && (
                  <div><span className="text-gray-400">Description:</span> <span className="text-gray-300">{editedData.orgData.description}</span></div>
                )}
              </div>
            )}
          </div>

          {/* Programs */}
          {editedData.programs && editedData.programs.length > 0 && (
            <div className="bg-gray-800/50 rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-white font-medium">Programs ({editedData.programs.length})</h4>
                <span className="text-sm text-gray-400">
                  {editedData.programs.reduce((total, p) => total + (p.sessions?.length || 0), 0)} sessions total
                </span>
              </div>
              <div className="space-y-2">
                {editedData.programs.map((program, idx) => (
                  <div key={idx} className="bg-gray-700/50 rounded p-3">
                    {isEditing ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={program.name || ''}
                            onChange={(e) => updateProgramField(idx, 'name', e.target.value)}
                            placeholder="Program Name"
                            className="flex-1 px-2 py-1 bg-gray-600 rounded text-white text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => removeProgram(idx)}
                            className="px-2 py-1 bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded text-xs transition-colors"
                            title="Delete program and all its sessions"
                          >
                            Delete Program
                          </button>
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={program.category || ''}
                            onChange={(e) => updateProgramField(idx, 'category', e.target.value)}
                            placeholder="Category"
                            className="flex-1 px-2 py-1 bg-gray-600 rounded text-white text-sm"
                          />
                          <input
                            type="text"
                            value={`${program.minAge || ''}-${program.maxAge || ''}`}
                            onChange={(e) => {
                              const [min, max] = e.target.value.split('-');
                              updateProgramField(idx, 'minAge', Number.parseInt(min, 10) || undefined);
                              updateProgramField(idx, 'maxAge', Number.parseInt(max, 10) || undefined);
                            }}
                            placeholder="Age Range (e.g., 5-12)"
                            className="w-24 px-2 py-1 bg-gray-600 rounded text-white text-sm"
                          />
                        </div>
                        <input
                          type="url"
                          value={program.registrationUrl || ''}
                          onChange={(e) => updateProgramField(idx, 'registrationUrl', e.target.value)}
                          placeholder="Program Registration URL (optional)"
                          className="w-full px-2 py-1 bg-gray-600 rounded text-white text-sm"
                        />
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={program.requiresInquiry || false}
                              onChange={(e) => updateProgramField(idx, 'requiresInquiry', e.target.checked)}
                              className="w-4 h-4 rounded border-gray-600 bg-gray-600 text-indigo-600 focus:ring-indigo-500"
                            />
                            Requires Inquiry (no regular sessions)
                          </label>
                        </div>
                        {program.requiresInquiry && (
                          <input
                            type="url"
                            value={program.inquiryUrl || ''}
                            onChange={(e) => updateProgramField(idx, 'inquiryUrl', e.target.value)}
                            placeholder="Inquiry Form URL"
                            className="w-full px-2 py-1 bg-gray-600 rounded text-white text-sm"
                          />
                        )}
                      </div>
                    ) : (
                      <div className="text-sm">
                        <div className="text-white font-medium">{program.name || 'Unnamed Program'}</div>
                        <div className="text-gray-400 text-xs">
                          {program.category && <span className="mr-2">{program.category}</span>}
                          {(program.minAge || program.maxAge) && (
                            <span>Ages {program.minAge || '?'}-{program.maxAge || '?'}</span>
                          )}
                        </div>
                      </div>
                    )}
                    {/* Sessions */}
                    <SessionEditor
                      sessions={program.sessions}
                      programIndex={idx}
                      isEditing={isEditing}
                      onUpdate={updateSessionField}
                      onAdd={addSession}
                      onRemove={removeSession}
                      duplicateWarnings={duplicateWarnings?.filter(dw => dw.programName === program.name)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Raw Extracted Data (Debug View) */}
          {job.status === 'review' && (
            <div className="bg-gray-800/50 rounded-lg p-4 mt-4">
              <details className="text-sm">
                <summary className="text-gray-400 cursor-pointer hover:text-white font-medium">
                  View Raw Extracted Data (Debug)
                </summary>
                <div className="mt-3 space-y-4">
                  {/* Organization Data */}
                  <div>
                    <h5 className="text-gray-500 text-xs uppercase tracking-wider mb-2">Organization</h5>
                    <pre className="bg-gray-900 p-3 rounded text-xs text-gray-300 overflow-x-auto">
                      {JSON.stringify(job.extractedOrgData, null, 2)}
                    </pre>
                  </div>
                  
                  {/* Programs with Sessions */}
                  <div>
                    <h5 className="text-gray-500 text-xs uppercase tracking-wider mb-2">
                      Programs ({job.extractedPrograms?.length || 0})
                    </h5>
                    {job.extractedPrograms?.map((program, idx) => (
                      <div key={idx} className="mb-3">
                        <div className="text-gray-400 text-xs mb-1">
                          {program.name || `Program ${idx + 1}`} - {program.sessions?.length || 0} sessions
                        </div>
                        <pre className="bg-gray-900 p-3 rounded text-xs text-gray-300 overflow-x-auto">
                          {JSON.stringify(program, null, 2)}
                        </pre>
                      </div>
                    ))}
                  </div>

                  {/* Confidence Scores */}
                  {job.confidenceScores && (
                    <div>
                      <h5 className="text-gray-500 text-xs uppercase tracking-wider mb-2">Confidence Scores</h5>
                      <pre className="bg-gray-900 p-3 rounded text-xs text-gray-300 overflow-x-auto">
                        {JSON.stringify(job.confidenceScores, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-700">
            <button
              type="button"
              onClick={handleLocalApprove}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg font-medium transition-colors"
            >
              <CheckCircle className="w-4 h-4" />
              Approve & Create
            </button>

            {job.organizationId && (
              <button
                type="button"
                onClick={checkForDuplicates}
                disabled={checkingDuplicates}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {checkingDuplicates ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
                Check Duplicates
              </button>
            )}

            {!showRejectForm ? (
              <button
                type="button"
                onClick={() => setShowRejectForm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg font-medium transition-colors"
              >
                <XCircle className="w-4 h-4" />
                Reject
              </button>
            ) : (
              <div className="flex gap-2 flex-1">
                <input
                  type="text"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Reason for rejection..."
                  className="flex-1 px-3 py-2 bg-gray-700 rounded-lg text-white text-sm"
                />
                <button
                  type="button"
                  onClick={handleReject}
                  disabled={!rejectReason.trim()}
                  className="px-4 py-2 bg-red-600 hover:bg-red-500 disabled:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                >
                  Confirm Reject
                </button>
                <button
                  type="button"
                  onClick={() => setShowRejectForm(false)}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Retry button for failed jobs */}
      {job.status === 'failed' && (
        <div className="pt-4 border-t border-gray-700">
          <button
            type="button"
            onClick={() => onRetry(job.id)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Retry Extraction
          </button>
        </div>
      )}

      {/* Rejection reason */}
      {job.status === 'rejected' && job.rejectionReason && (
        <div className="mt-4 pt-4 border-t border-gray-700">
          <p className="text-red-400 text-sm">
            <span className="font-medium">Rejection reason:</span> {job.rejectionReason}
          </p>
          <button
            type="button"
            onClick={() => onRetry(job.id)}
            className="mt-2 flex items-center gap-2 px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 rounded text-sm font-medium transition-colors"
          >
            <RefreshCw className="w-3 h-3" />
            Retry
          </button>
        </div>
      )}

      {/* Approved - View Organization CTA */}
      {job.status === 'approved' && job.organizationId && (
        <div className="mt-4 pt-4 border-t border-gray-700">
          <p className="text-green-400 text-sm mb-2">
            <CheckCircle className="w-4 h-4 inline mr-1" />
            <span className="font-medium">Data has been added to the organization</span>
          </p>
          <Link
            to={`/org-management?orgId=${job.organizationId}`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg font-medium transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            View Organization
          </Link>
        </div>
      )}
    </div>
  );
}

export function IngestionReview() {
  const { user, getAccessToken } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('review');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const loadJobs = useCallback(async (status, reset = false) => {
    try {
      setLoading(true);
      const token = getAccessToken();
      const newOffset = reset ? 0 : offset;
      
      const response = await fetch(
        `${API_URL}/v2/ingestion/jobs?status=${status}&limit=20&offset=${newOffset}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      if (!response.ok) throw new Error('Failed to load jobs');
      
      const data = await response.json();
      
      if (reset) {
        setJobs(data.jobs);
        setOffset(20);
      } else {
        setJobs(prev => [...prev, ...data.jobs]);
        setOffset(prev => prev + 20);
      }
      
      setHasMore(data.jobs.length === 20);
    } catch (error) {
      console.error('Error loading jobs:', error);
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, offset]);

  const loadStats = useCallback(async () => {
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_URL}/v2/ingestion/stats`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      
      if (!response.ok) throw new Error('Failed to load stats');
      
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, [getAccessToken]);

  useEffect(() => {
    loadJobs(activeTab, true);
    loadStats();
  }, [activeTab]);

  const handleApprove = async (jobId, data) => {
    console.log('Approving job with data:', JSON.stringify(data, null, 2));
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_URL}/v2/ingestion/jobs/${jobId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          ...data,
          reviewedBy: user?.id || user?.email || 'admin',
        }),
      });

      if (!response.ok) throw new Error('Failed to approve');
      
      const result = await response.json();
      
      // Show success message with details
      let message = `Successfully approved!\n\n`;
      message += `Created: ${result.created.programs} programs, ${result.created.sessions} sessions\n`;
      if (result.updated.programs > 0) {
        message += `Updated: ${result.updated.programs} programs\n`;
      }
      if (result.skipped?.sessions > 0) {
        message += `Skipped: ${result.skipped.sessions} duplicate sessions\n`;
      }
      if (result.errors.length > 0) {
        const warnings = result.errors.filter(e => e.includes('Warning:'));
        const errors = result.errors.filter(e => !e.includes('Warning:') && !e.includes('Skipped'));
        
        if (warnings.length > 0) {
          message += `\nWarnings (${warnings.length}):\n${warnings.slice(0, 3).join('\n')}`;
        }
        if (errors.length > 0) {
          message += `\nErrors (${errors.length}):\n${errors.slice(0, 3).join('\n')}`;
        }
      }
      
      alert(message);
      
      // Refresh jobs
      loadJobs(activeTab, true);
      loadStats();
    } catch (error) {
      console.error('Error approving job:', error);
      alert('Failed to approve: ' + error.message);
    }
  };

  const handleReject = async (jobId, reason) => {
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_URL}/v2/ingestion/jobs/${jobId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          reason,
          reviewedBy: user?.id || user?.email || 'admin',
        }),
      });

      if (!response.ok) throw new Error('Failed to reject');
      
      loadJobs(activeTab, true);
      loadStats();
    } catch (error) {
      console.error('Error rejecting job:', error);
      alert('Failed to reject: ' + error.message);
    }
  };

  const handleRetry = async (jobId) => {
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_URL}/v2/ingestion/jobs/${jobId}/retry`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) throw new Error('Failed to retry');
      
      loadJobs(activeTab, true);
      loadStats();
    } catch (error) {
      console.error('Error retrying job:', error);
      alert('Failed to retry: ' + error.message);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
      </div>
    );
  }

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Globe className="w-6 h-6 text-indigo-400" />
          Website Ingestion Review
        </h1>
      </div>

      {/* Stats Dashboard */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
          {[
            { label: 'Queued', value: stats.queued, color: 'text-gray-400' },
            { label: 'In Progress', value: stats.inProgress, color: 'text-blue-400' },
            { label: 'Needs Review', value: stats.review, color: 'text-yellow-400' },
            { label: 'Approved', value: stats.approved, color: 'text-green-400' },
            { label: 'Rejected', value: stats.rejected, color: 'text-red-400' },
            { label: 'Failed', value: stats.failed, color: 'text-red-400' },
          ].map((stat) => (
            <div key={stat.label} className="glass-card p-3 text-center">
              <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
              <div className="text-gray-400 text-xs">{stat.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-700 pb-2">
        {[
          { id: 'review', label: 'Needs Review', count: stats?.review },
          { id: 'queued', label: 'Queued', count: stats?.queued },
          { id: 'failed', label: 'Failed', count: stats?.failed },
          { id: 'approved', label: 'Approved', count: stats?.approved },
          { id: 'rejected', label: 'Rejected', count: stats?.rejected },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-indigo-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-800'
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-gray-700 rounded-full text-xs">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Jobs List */}
      <div>
        {loading && jobs.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-12">
            <BarChart3 className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No {activeTab} jobs found</p>
          </div>
        ) : (
          <>
            {jobs.map((job) => (
              <ReviewCard
                key={job.id}
                job={job}
                onApprove={handleApprove}
                onReject={handleReject}
                onRetry={handleRetry}
                getAccessToken={getAccessToken}
              />
            ))}
            
            {hasMore && (
              <div className="text-center py-4">
                <button
                  type="button"
                  onClick={() => loadJobs(activeTab)}
                  disabled={loading}
                  className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                      Loading...
                    </>
                  ) : (
                    'Load More'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default IngestionReview;
